import type { CatalogCategory, CatalogProduct } from '../types'
import { parsePathSegments } from './pathUtils'
import { buildProductPathFromIdentity } from './productPaths'

const buildCategorySlugTrail = (categories: CatalogCategory[], categoryId: string | null | undefined) => {
  if (!categoryId) return []
  const byId = new Map(categories.map((category) => [category._id, category]))
  const trail: string[] = []
  const visited = new Set<string>()
  let currentId: string | null = categoryId
  let guard = 0

  while (currentId && guard < 20) {
    guard += 1
    if (visited.has(currentId)) break
    visited.add(currentId)
    const current = byId.get(currentId)
    if (!current?.slug) break
    trail.unshift(current.slug)
    currentId = current.parentCategoryId
  }

  return trail
}

export const buildCanonicalProductPath = (product: CatalogProduct, categories: CatalogCategory[]) => {
  const primaryCategoryId = product.categoryIds?.[0] || product.categoryId
  const categoryTrail = buildCategorySlugTrail(categories, primaryCategoryId)
  return buildProductPathFromIdentity({
    publicCode: product.publicCode,
    slug: product.slug,
    categorySlugs: categoryTrail,
  })
}

export const buildCategoryPathFromTrail = (trail: CatalogCategory[]) => {
  const encodedSegments = trail
    .map((category) => category.slug.trim())
    .filter(Boolean)
    .map((segment) => encodeURIComponent(segment))
  return encodedSegments.length ? `/${encodedSegments.join('/')}` : '/'
}

export const resolveCategoryFromPath = (categories: CatalogCategory[], path: string) => {
  const pathSegments = parsePathSegments(path)
  if (!pathSegments.length) return null

  const childrenByParent = new Map<string, CatalogCategory[]>()
  const ROOT_PARENT_KEY = '__root__'

  categories.forEach((category) => {
    const parentKey = category.parentCategoryId ?? ROOT_PARENT_KEY
    const list = childrenByParent.get(parentKey) ?? []
    list.push(category)
    childrenByParent.set(parentKey, list)
  })

  const trail: CatalogCategory[] = []
  let currentParentKey = ROOT_PARENT_KEY

  for (const segment of pathSegments) {
    const directChildren = childrenByParent.get(currentParentKey) ?? []
    const match = directChildren.find((category) => category.slug === segment)
    if (!match) return null
    trail.push(match)
    currentParentKey = match._id
  }

  if (!trail.length) return null
  return {
    category: trail[trail.length - 1],
    trail,
  }
}

export const getCategoryImageUrl = (category: CatalogCategory) =>
  category.imageVariants?.large ?? category.imageVariants?.medium ?? category.imageVariants?.small ?? ''

const CATEGORY_IMAGE_VARIANTS: Array<{ key: 'small' | 'medium' | 'large'; width: number }> = [
  { key: 'small', width: 360 },
  { key: 'medium', width: 720 },
  { key: 'large', width: 1400 },
]

export const getCategoryImageSources = (
  category: CatalogCategory,
  sizes = '(min-width: 1024px) 100vw, 100vw',
): { src: string; srcSet?: string; sizes?: string } => {
  const src = getCategoryImageUrl(category)
  const variants = category.imageVariants
  if (!variants) return { src }
  const seenUrls = new Set<string>()
  const srcSetEntries: string[] = []
  for (const { key, width } of CATEGORY_IMAGE_VARIANTS) {
    const variantUrl = variants[key]
    if (!variantUrl || seenUrls.has(variantUrl)) continue
    seenUrls.add(variantUrl)
    srcSetEntries.push(`${variantUrl} ${width}w`)
  }
  return srcSetEntries.length > 0 ? { src, srcSet: srcSetEntries.join(', '), sizes } : { src }
}
